import React, { type ReactElement, type ReactNode } from "react";
import type { BaseProps } from "@purpur/common-types";
import { Container } from "@purpur/container";
import { Link, type LinkProps } from "@purpur/link";
import { Select } from "@purpur/select";
import { type ColorOptions, type ColorScheme, useColorScheme, useTheme } from "@purpur/theme";
import c from "classnames/bind";

import { rootClassName } from "./constants";
import styles from "./footer.module.scss";
import {
  type CustomLinkType,
  FooterNavigation,
  type FooterNavigationItem,
  type FooterNavigationItemLink,
} from "./footer-navigation";
import { FooterSocialLink, type FooterSocialLinkProps } from "./footer-social-link";
import { TeliaLogo } from "./telia-logo";

const cx = c.bind(styles);

type AdditionalLink = FooterNavigationItemLink &
  Omit<LinkProps, "href" | "variant" | "negative" | "children" | "disableVisited">;

type SectionProps = Omit<BaseProps<"section">, "children">;

export type FooterProps = BaseProps<"footer"> & {
  /**
   * Navigation section of the footer.
   */
  navigation?: {
    customLink?: CustomLinkType;
    links: FooterNavigationItem[];
    sectionProps?: SectionProps;
  };
  /**
   * Social links section of the footer.
   */
  social?: {
    links: FooterSocialLinkProps[];
    sectionProps?: SectionProps;
  };
  /**
   * Contacts section of the footer.
   */
  contacts?: {
    copyright?: string;
    address?: {
      content: ReactElement;
      addressProps?: Omit<BaseProps<"address">, "children">;
    };
    sectionProps?: SectionProps;
  };
  /**
   * Additional links section of the footer.
   */
  additional?: {
    links: AdditionalLink[];
    sectionProps?: SectionProps;
  };
  /**
   * Custom content section of the footer.
   */
  custom?: {
    content: ReactElement;
    sectionProps?: SectionProps;
  };
  /**
   * Payment images section of the footer.
   */
  payments?: {
    /**
     * @deprecated use content instead
     */
    images?: { src: string; alt: string }[];
    content?: ReactElement | ReactElement[];
    sectionProps?: SectionProps;
  };
  /**
   * Custom logo that replaces the default Telia logo.
   */
  logo?: ReactNode;
  themeSelect?: {
    label: string;
    options: { value: ColorOptions; label: string }[];
  };
  forceColorScheme?: ColorScheme;
};

export const Footer = ({
  className,
  navigation,
  social,
  contacts,
  additional,
  custom,
  payments,
  logo,
  themeSelect,
  forceColorScheme,
  ...props
}: FooterProps) => {
  const classes = cx(className, rootClassName);

  const { options } = useTheme();
  const [colorScheme, setColorScheme] = useColorScheme();

  const resolvedColorScheme = forceColorScheme ?? options?.footerColorScheme;

  return (
    <footer data-color-scheme={resolvedColorScheme} className={classes} {...props}>
      <Container>
        <div className={cx(`${rootClassName}__container`)}>
          <div
            className={cx(`${rootClassName}__logo`, {
              [`${rootClassName}__logo--telia`]: !logo,
            })}
          >
            {logo ?? <TeliaLogo />}
          </div>
          {social && (
            <section
              {...social.sectionProps}
              className={cx(
                `${rootClassName}__social`,
                `${rootClassName}__social--md`,
                social.sectionProps?.className
              )}
            >
              {social.links.map(({ icon, href, ...socialLinkProps }) => (
                <FooterSocialLink {...socialLinkProps} icon={icon} href={href} key={href} />
              ))}
            </section>
          )}
          {navigation && (
            <FooterNavigation
              customLink={navigation.customLink}
              navigationLinks={navigation.links}
              sectionProps={navigation.sectionProps}
            />
          )}
          {payments && (
            <section
              {...payments.sectionProps}
              className={cx(`${rootClassName}__payments`, payments.sectionProps?.className)}
            >
              {payments.content}
              {payments.images?.map(({ alt, src }) => (
                <img
                  className={cx(`${rootClassName}__payments-image`)}
                  src={src}
                  alt={alt}
                  key={src}
                />
              ))}
            </section>
          )}
          {themeSelect && (
            <section className={cx(`${rootClassName}__theme-select`)}>
              <Select
                value={colorScheme}
                label={themeSelect.label}
                options={themeSelect.options}
                negative
                onChange={(event) => {
                  setColorScheme(event.target.value as ColorOptions);
                }}
              />
            </section>
          )}
          {contacts && (
            <section
              {...contacts.sectionProps}
              className={cx(`${rootClassName}__contacts`, contacts.sectionProps?.className)}
            >
              {contacts.copyright && (
                <div className={cx(`${rootClassName}__contacts-copyright`)}>
                  {contacts.copyright}
                </div>
              )}
              {contacts.address && (
                <address
                  {...contacts.address.addressProps}
                  className={cx(
                    `${rootClassName}__contacts-address`,
                    contacts.address.addressProps?.className
                  )}
                >
                  {contacts.address.content}
                </address>
              )}
            </section>
          )}
          {(custom || additional) && (
            <div className={cx(`${rootClassName}__additional`)}>
              {custom && (
                <section
                  {...custom.sectionProps}
                  className={cx(`${rootClassName}__custom-content`, custom.sectionProps?.className)}
                >
                  {custom.content}
                </section>
              )}
              {additional && (
                <section
                  {...additional.sectionProps}
                  className={cx(
                    `${rootClassName}__additional-links`,
                    additional.sectionProps?.className
                  )}
                >
                  {additional?.links?.map(({ href, text, ...additionalLinkProps }) => (
                    <Link
                      {...additionalLinkProps}
                      variant="standalone"
                      negative
                      href={href}
                      key={href}
                      disableVisited={true}
                    >
                      {text}
                    </Link>
                  ))}
                </section>
              )}
            </div>
          )}
          {social && (
            <section
              {...social.sectionProps}
              className={cx(
                `${rootClassName}__social`,
                `${rootClassName}__social--sm`,
                social.sectionProps?.className
              )}
            >
              {social.links.map(({ icon, href, ...socialLinkProps }) => (
                <FooterSocialLink {...socialLinkProps} icon={icon} href={href} key={href} />
              ))}
            </section>
          )}
        </div>
      </Container>
    </footer>
  );
};

Footer.displayName = "Footer";
